import { Combobox, FormControl, FormField, FormInput, FormItem, FormLabel, FormMessage, Text } from "@sentricmusic/ui";
import { Fragment, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import countries from "@/data/countries";

import type { SectionKey } from "../types/gigs.types";
import type { GigClaimFormValues } from "../utils/gigsClaimSchema";

import { PROMOTOR_FORM_ITEMS } from "../utils/gigsClaimConstants";
import { FLAGS } from "../utils/gigsClaimUtils";

function PromoterSection() {
  const { t } = useTranslation(["gigs", "countries"]);
  const { control } = useFormContext<GigClaimFormValues>();
  const country = useWatch({ control, name: "country" });

  const sections = useMemo<SectionKey[]>(() => {
    const flag = FLAGS.find(f => f.id === country) ?? FLAGS.find(f => f.id === "default")!;
    return flag.onSections;
  }, [country]);

  const hasPromoterFields = sections.includes("promotor") || sections.includes("promotorInfo");

  if (!hasPromoterFields)
    return null;

  return (
    <div>
      {sections.includes("promotor") && (
        <div>
          <Text as="subtitle">{t("gigs:promoter-placeholder", "Promoter")}</Text>
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name="promotor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("gigs:promoter-placeholder", "Promoter")}</FormLabel>
                  <FormControl>
                    <FormInput
                      placeholder={t("gigs:promoter-placeholder", "Promoter") ?? "Promoter"}
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
      {sections.includes("promotorInfo") && (
        <div>
          <Text as="subtitle">{t("gigs:promoter-info-header", "Promoter Information")}</Text>
          <div className="flex flex-col gap-2">
            {PROMOTOR_FORM_ITEMS.map(p => (
              <Fragment key={p.name}>
                {p.type !== "select" && (
                  <FormField
                    control={control}
                    name={p.name as keyof GigClaimFormValues}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t(p.tKey, p.defaultValue)}</FormLabel>
                        <FormControl>
                          <FormInput
                            type={p.type === "email" ? "email" : "text"}
                            placeholder={t(p.tKey, p.defaultValue) ?? p.defaultValue}
                            {...field}
                            value={(field.value as string | undefined) ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                {p.type === "select" && (
                  <FormField
                    control={control}
                    name={p.name as keyof GigClaimFormValues}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t(p.tKey, p.defaultValue)}</FormLabel>
                        <FormControl>
                          <Combobox
                            placeholder={t(p.tKey, p.defaultValue) ?? p.defaultValue}
                            value={(field.value as string | "") ?? ""}
                            onValueChange={field.onChange}
                            data={countries.map(option => ({
                              label: t(`countries:${option.name}`),
                              value: option.value,
                            }))}
                            searchByLabel
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </Fragment>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

export default PromoterSection;
